import {
  AvailableOptions,
  CartResponse,
  CartResponseFormatted,
  ICartItem,
  PromotionProduct,
} from '@/types/cart'
import { WishlistResponse, WishlistResponseFormatted } from '@/types/wishlist'

type OptionsType = {
  option1: string
  option2: string
  option3: string
  option4: string
  [key: string]: string
}

const reFormatCartResponse = (data: CartResponse): CartResponseFormatted => {
  if (!data || !data.items) {
    return {
      ...data,
      items: [],
      original: data,
      promotionProducts: [],
    } as CartResponseFormatted
  }

  const formattedItems = formatAvailableOptions(data.items)
  const formattedItemsWithNestedGift = formatNestedGift(formattedItems)
  // Remove nestedGift from items
  const items = formattedItemsWithNestedGift.filter(
    (item) => !item.index_parent_id
  )

  const promotionProducts = formatPromotionProducts(data.promotion_products)

  return {
    ...data,
    items,
    promotionProducts,
    original: data,
  } as CartResponseFormatted
}

const reFormatWishlistResponse = (
  data: WishlistResponse
): WishlistResponseFormatted => {
  if (!data || !data.data) {
    return {
      ...data,
      data: [],
    } as WishlistResponseFormatted
  }

  const formattedItems = formatAvailableOptions(data.data)

  return {
    ...data,
    data: formattedItems,
  } as WishlistResponseFormatted
}

const formatAvailableOptions = <T extends ICartItem>(
  items: T[]
): (T & { availableOptions: AvailableOptions; is_sold_out?: boolean })[] => {
  if (!items || items.length === 0)
    return [] as (T & {
      availableOptions: AvailableOptions
      is_sold_out?: boolean
    })[]

  const formattedItems = items.map((item) => {
    const options: OptionsType = {
      option1: '',
      option2: '',
      option3: '',
      option4: '',
    }

    const availableOptions =
      item.options?.map((option, index) => {
        const optionKey = `option${index + 1}` as keyof OptionsType
        const existVariant =
          item.variants?.[optionKey as keyof typeof item.variants]
        const variantValue =
          typeof existVariant === 'string' ? existVariant : ''

        if (variantValue) {
          options[optionKey] = variantValue
        } else {
          delete options[optionKey]
        }

        return {
          index,
          key: optionKey,
          option: option.values,
          value: variantValue,
        }
      }) || []

    return {
      ...item,
      options,
      availableOptions,
    } as T & { availableOptions: AvailableOptions }
  })

    // After formatting, filter out hidden variants and mark items as sold out
  return formattedItems.map(filterItemVariants)
}

const filterItemVariants = <T extends ICartItem>(item: T & { availableOptions: AvailableOptions }) => {
  const variantTitle = item.variants?.title
  const is_sold_out = item.variants_hide?.includes(variantTitle) || false

  const availableOptions = item.availableOptions.map((option) => {
    const optionKey = option.key as keyof OptionsType
    const currentValue = option.value
    const filteredValues = option.option.filter((value) => {
      // Keep the currently selected value
      if (value === currentValue) return true

      // Handle color options
      if (optionKey === 'option1' && item.variants?.option2) {
        const combination = `${value} / ${item.variants.option2}`
        return !item.variants_hide?.includes(combination) || value === currentValue
      }

      // Handle size options
      if (optionKey === 'option2' && item.variants?.option1) {
        const combination = `${item.variants.option1} / ${value}`
        return !item.variants_hide?.includes(combination) || value === currentValue
      }

      return true
    })

    return { ...option, option: filteredValues }
  })

  return { ...item, is_sold_out, availableOptions }
}

const formatNestedGift = (itemsWithAvailableOptions: ICartItem[]) => {
  const giftItems = itemsWithAvailableOptions.filter(
    (item) => item?.index_parent_id
  )

  const formattedItems = itemsWithAvailableOptions.map((item) => {
    // Xử lý nestedGift vào trong item
    if (giftItems.length > 0) {
      const indexId = item.index_id ?? `${item.id}-${item.variants?._id}`
      item.nestedGift = giftItems.filter(
        (giftItem) => giftItem.index_parent_id === indexId
      )
    }

    return item
  })

  return formattedItems
}

const formatPromotionProducts = (promotionProducts: PromotionProduct[]) => {
  if (!promotionProducts || promotionProducts.length === 0) return []

  return promotionProducts.map((product) => {
    return {
      ...product,
    }
  })
}
export const CartFormatter = {
  reFormatCartResponse,
  reFormatWishlistResponse,
}
